import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Clock, CheckCircle, XCircle } from 'lucide-react';
import supportTicketService from '../../services/supportTicketService';
import toast from 'react-hot-toast';

const MyTickets = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      const response = await supportTicketService.getMyTickets();
      setTickets(response.data.data || response.data || []);
    } catch (error) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      open: { label: 'Open', color: 'bg-blue-100 text-blue-700', icon: Clock },
      in_progress: { label: 'In Progress', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
      resolved: { label: 'Resolved', color: 'bg-green-100 text-green-700', icon: CheckCircle },
      closed: { label: 'Closed', color: 'bg-gray-100 text-gray-700', icon: XCircle }
    };
    return badges[status] || badges.open;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      urgent: 'text-red-600 font-semibold',
      high: 'text-orange-600 font-semibold',
      medium: 'text-blue-600',
      low: 'text-gray-600'
    };
    return colors[priority] || colors.medium;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Support Tickets</h1>
          <p className="text-gray-600 mt-1">View and manage your support requests</p>
        </div>
        <button
          onClick={() => navigate('/support/create')}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-4 w-4" />
          New Ticket
        </button>
      </div>

      {tickets.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <p className="text-gray-500 mb-4">No support tickets yet</p>
          <button
            onClick={() => navigate('/support/create')}
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            Create your first ticket
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {tickets.map((ticket) => {
            const statusBadge = getStatusBadge(ticket.status);
            const StatusIcon = statusBadge.icon;
            
            return (
              <div
                key={ticket.id}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      {ticket.subject}
                    </h3>
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {ticket.description}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusBadge.color} flex items-center gap-1`}>
                      <StatusIcon className="h-3 w-3" />
                      {statusBadge.label}
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-4">
                    <span className={`capitalize ${getPriorityColor(ticket.priority)}`}>
                      {ticket.priority} Priority
                    </span>
                    <span className="text-gray-500">
                      {new Date(ticket.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <span className="text-gray-500">
                    Ticket #{ticket.id}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyTickets;
